import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { DashboardLayout, workerNavItems, teamLeaderNavItems } from "@/components/DashboardLayout";
import {
  FileText,
  Download,
  CheckCircle2,
  Clock,
  PenTool,
  AlertCircle,
  Loader2,
  User,
  Building2,
  FileSignature,
  Trash2,
  UserCircle,
  MapPin,
  Phone,
  CreditCard,
  Globe,
  Hash,
  ArrowLeft
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EmploymentContract() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [profile, setProfile] = useState(null);
  const [holdingCompany, setHoldingCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  
  // Determine nav items based on user role
  const navItems = user?.role === "team_leader" ? teamLeaderNavItems : workerNavItems;
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    fetchContractAndProfile();
  }, []);

  const fetchContractAndProfile = async () => {
    try {
      // Fetch contract, profile and holding company in parallel
      const [contractRes, profileRes, holdingRes] = await Promise.all([
        axios.get(`${API}/employment-contracts/my`),
        axios.get(`${API}/workers/me/profile`),
        axios.get(`${API}/holding`).catch(() => ({ data: null }))
      ]);
      
      if (contractRes.data.contract) {
        setContract(contractRes.data.contract);
      } else {
        // Create contract if doesn't exist
        await axios.post(`${API}/employment-contracts`);
        const newRes = await axios.get(`${API}/employment-contracts/my`);
        setContract(newRes.data.contract);
      }
      
      if (profileRes.data) {
        setProfile(profileRes.data);
      }
      
      if (holdingRes.data && holdingRes.data.profile) {
        setHoldingCompany(holdingRes.data);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Build personal info from profile data
  const getPersonalInfoFromProfile = () => {
    if (!profile && !user) return null;
    
    const personalNumber = profile?.norwegian_id || profile?.d_number || "";
    const fullAddress = [
      profile?.address,
      profile?.postal_code,
      profile?.city
    ].filter(Boolean).join(", ");
    
    return {
      full_name: user?.name || "",
      personal_number: personalNumber,
      address: fullAddress,
      phone: user?.phone || profile?.phone || "",
      bank_account: profile?.bank_account || "",
      nationality: profile?.nationality || ""
    };
  };

  // Check if profile has required info for contract
  const isProfileComplete = () => {
    const info = getPersonalInfoFromProfile();
    return info && info.full_name && info.personal_number && info.address;
  };

  // Canvas functions
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  useEffect(() => {
    if (showSignaturePad && canvasRef.current) {
      initCanvas();
    }
  }, [showSignaturePad]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCoordinates(e);
    
    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    initCanvas();
    setHasSignature(false);
  };

  // Validate personal info before signing
  const validatePersonalInfo = (info) => {
    if (!info?.full_name?.trim()) return t('errors.requiredField');
    if (!info?.personal_number?.trim()) return t('errors.requiredField');
    if (!info?.address?.trim()) return t('errors.requiredField');
    return null;
  };

  const handleSign = async () => {
    if (!hasSignature) {
      toast.error(t('sja.clearSignature'));
      return;
    }
    
    // Get personal info from profile
    const personalInfo = getPersonalInfoFromProfile();
    if (!personalInfo || !personalInfo.full_name || !personalInfo.personal_number || !personalInfo.address) {
      toast.error(t('contracts.completeProfileFirst'));
      return;
    }

    setSigning(true);
    try {
      const canvas = canvasRef.current;
      const signatureImage = canvas.toDataURL("image/png");
      
      await axios.post(`${API}/employment-contracts/${contract.contract_id}/worker-sign`, {
        signature_image: signatureImage,
        terms_accepted: true,
        personal_info: personalInfo
      });
      
      toast.success(t('success.saved'));
      setShowSignaturePad(false);
      fetchContractAndProfile();
    } catch (err) {
      console.error("Sign error:", err);
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSigning(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/employment-contracts/${contract.contract_id}/pdf`,
        { responseType: "blob" }
      );
      
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `employment_contract_${contract.contract_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('success.downloaded'));
    } catch (err) {
      console.error("Download error:", err);
      toast.error(t('errors.generic'));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" /> {t('common.pending')}</Badge>;
      case "worker_signed":
        return <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/30"><User className="w-3 h-3 mr-1" /> {t('contracts.pendingContracts')}</Badge>;
      case "hr_signed":
        return <Badge variant="outline" className="bg-purple-500/10 text-purple-500 border-purple-500/30"><Building2 className="w-3 h-3 mr-1" /> {t('contracts.signContract')}</Badge>;
      case "fully_signed":
        return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/30"><CheckCircle2 className="w-3 h-3 mr-1" /> {t('contracts.signed')}</Badge>;
      case "rejected":
        return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30"><AlertCircle className="w-3 h-3 mr-1" /> {t('common.rejected')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Request new contract after rejection
  const handleRequestNewContract = async () => {
    try {
      setSigning(true);
      await axios.post(`${API}/employment-contracts/request-new`);
      toast.success(t('success.sent'));
      fetchContractAndProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout navItems={navItems} title={t('contracts.employmentContract')}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#23F7DD]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout navItems={navItems} title={t('contracts.employmentContract')}>
      <div className="space-y-6" data-testid="employment-contract-page">
        {/* Back Button */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(user?.role === "team_leader" ? "/team-leader/profile" : "/worker/profile")}
            className="text-[#94A3B8] hover:text-white"
            data-testid="back-to-profile-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')} {t('nav.profile')}
          </Button>
        </div>
        
        <div>
          <h1 className="text-2xl font-bold text-white">Employment Contract</h1>
          <p className="text-[#94A3B8]">Your official employment agreement with Doserne Utleie AS</p>
        </div>

      {/* Rejected Contract Message */}
      {contract?.status === "rejected" && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-400">Contract Rejected</h3>
                <p className="text-[#94A3B8] mt-1 mb-3">
                  Your employment contract has been rejected by HR.
                  {contract.rejection_reason && (
                    <span className="block mt-2 p-3 bg-white/5 rounded-lg text-sm">
                      <strong className="text-white">Reason:</strong> {contract.rejection_reason}
                    </span>
                  )}
                </p>
                <p className="text-sm text-[#94A3B8] mb-4">
                  You can request a new contract after updating your profile information or addressing the issues mentioned above.
                </p>
                <Button
                  onClick={handleRequestNewContract}
                  disabled={signing}
                  className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                  data-testid="request-new-contract-btn"
                >
                  {signing ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Requesting...
                    </span>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Request New Contract
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {contract ? (
        <div className="grid gap-6">
          {/* Success Message when fully signed */}
          {contract.status === "fully_signed" && (
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-green-400">🎉 You&apos;re Ready to Work!</h3>
                    <p className="text-[#94A3B8]">
                      Your employment contract has been approved. You can now apply for projects and start earning!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contract Status Card */}
          <Card className="bg-[#0F0F0F] border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#23F7DD]/10 rounded-lg flex items-center justify-center">
                    <FileSignature className="w-6 h-6 text-[#23F7DD]" />
                  </div>
                  <div>
                    <CardTitle className="text-white">Employment Agreement</CardTitle>
                    <CardDescription>Fast ansettelse uten garantert stillingsprosent</CardDescription>
                  </div>
                </div>
                {getStatusBadge(contract.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Employer Information - Holding Company */}
              <div className="p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-emerald-400" />
                  Employer Details
                </h3>
                {holdingCompany?.profile ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-[#64748B] mb-1">Company Name</p>
                      <p className="text-white font-medium">{holdingCompany.profile.company_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B] mb-1">Org. Number</p>
                      <p className="text-white font-mono">{holdingCompany.profile.org_number}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B] mb-1">Address</p>
                      <p className="text-white">
                        {holdingCompany.profile.address}, {holdingCompany.profile.postal_code} {holdingCompany.profile.city}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B] mb-1">Legal Representative</p>
                      <p className="text-white">
                        {holdingCompany.profile.legal_representative}
                        {holdingCompany.profile.legal_rep_title && (
                          <span className="text-[#94A3B8] text-sm"> - {holdingCompany.profile.legal_rep_title}</span>
                        )}
                      </p>
                    </div>
                    {holdingCompany.profile.email && (
                      <div>
                        <p className="text-xs text-[#64748B] mb-1">Contact Email</p>
                        <p className="text-white">{holdingCompany.profile.email}</p>
                      </div>
                    )}
                    {holdingCompany.profile.phone && (
                      <div>
                        <p className="text-xs text-[#64748B] mb-1">Contact Phone</p>
                        <p className="text-white">{holdingCompany.profile.phone}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-[#94A3B8]">Doserne Utleie AS</p>
                )}
              </div>

              {/* Signature Status */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Signature Status</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Worker Signature */}
                  <div className={`p-4 rounded-lg border ${contract.worker_signed ? 'bg-green-500/10 border-green-500/30' : 'bg-white/5 border-white/10'}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <User className={`w-5 h-5 ${contract.worker_signed ? 'text-green-500' : 'text-[#94A3B8]'}`} />
                      <span className="text-white font-medium">Employee Signature</span>
                    </div>
                    {contract.worker_signed ? (
                      <div>
                        <p className="text-sm text-green-500">✓ Signed on {new Date(contract.worker_signed_at).toLocaleDateString()}</p>
                        {contract.worker_signature_data && (
                          <img src={contract.worker_signature_data} alt="Signature" className="mt-2 max-h-12 bg-white rounded" />
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-[#94A3B8]">Not yet signed</p>
                    )}
                  </div>

                  {/* HR Signature */}
                  <div className={`p-4 rounded-lg border ${contract.hr_signed ? 'bg-green-500/10 border-green-500/30' : 'bg-white/5 border-white/10'}`}>
                    <div className="flex items-center gap-3 mb-2">
                      <Building2 className={`w-5 h-5 ${contract.hr_signed ? 'text-green-500' : 'text-[#94A3B8]'}`} />
                      <span className="text-white font-medium">HR / Admin Signature</span>
                    </div>
                    {contract.hr_signed ? (
                      <div>
                        <p className="text-sm text-green-500">✓ Signed by {contract.hr_signed_by_name}</p>
                        <p className="text-xs text-[#94A3B8]">{new Date(contract.hr_signed_at).toLocaleDateString()}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-[#94A3B8]">Awaiting HR signature</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4">
                {!contract.worker_signed && (
                  <Button
                    onClick={() => setShowSignaturePad(true)}
                    disabled={!termsAccepted}
                    className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="sign-contract-btn"
                    title={!termsAccepted ? "Please accept the terms and conditions first" : "Sign your contract"}
                  >
                    <PenTool className="w-4 h-4 mr-2" />
                    Sign Contract
                  </Button>
                )}
                
                {contract.status === "fully_signed" ? (
                  <Button
                    onClick={handleDownloadPDF}
                    variant="outline"
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                    data-testid="download-contract-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Signed Contract
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    disabled
                    className="border-white/10 text-[#94A3B8] cursor-not-allowed"
                    title="PDF available after both parties sign"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF Available After Signing
                  </Button>
                )}
              </div>
              
              {/* Contract Details - At the bottom */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#23F7DD]" />
                  Contract Details
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-sm text-[#94A3B8] mb-1">Contract ID</p>
                    <p className="text-white font-mono">{contract.contract_id}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-sm text-[#94A3B8] mb-1">Position</p>
                    <p className="text-white">{contract.position}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-sm text-[#94A3B8] mb-1">Employment Type</p>
                    <p className="text-white">Permanent (On-Call)</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-lg">
                    <p className="text-sm text-[#94A3B8] mb-1">Created</p>
                    <p className="text-white">{new Date(contract.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information Section - Read from Profile */}
          {!contract.worker_signed && (
            <Card className="bg-[#0F0F0F] border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <UserCircle className="w-5 h-5 text-[#23F7DD]" />
                      Personal Information
                    </CardTitle>
                    <CardDescription>
                      This information is pulled from your profile and will be included in your contract.
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = '/worker/profile'}
                    className="border-[#23F7DD]/30 text-[#23F7DD] hover:bg-[#23F7DD]/10"
                  >
                    <PenTool className="w-4 h-4 mr-2" />
                    Edit in Profile
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {(() => {
                  const info = getPersonalInfoFromProfile();
                  const isComplete = isProfileComplete();
                  
                  return (
                    <>
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Full Name */}
                        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                          <div className="flex items-center gap-2 mb-1">
                            <User className="w-4 h-4 text-[#23F7DD]" />
                            <span className="text-xs text-[#64748B]">Full Name <span className="text-red-400">*</span></span>
                          </div>
                          <p className={`font-medium ${info?.full_name ? 'text-white' : 'text-red-400 italic'}`}>
                            {info?.full_name || "Not provided"}
                          </p>
                        </div>
                        
                        {/* Personal Number */}
                        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                          <div className="flex items-center gap-2 mb-1">
                            <Hash className="w-4 h-4 text-[#23F7DD]" />
                            <span className="text-xs text-[#64748B]">Personal Number <span className="text-red-400">*</span></span>
                          </div>
                          <p className={`font-medium ${info?.personal_number ? 'text-white' : 'text-red-400 italic'}`}>
                            {info?.personal_number || "Not provided"}
                          </p>
                        </div>
                        
                        {/* Address */}
                        <div className="p-3 bg-white/5 rounded-lg border border-white/10 md:col-span-2">
                          <div className="flex items-center gap-2 mb-1">
                            <MapPin className="w-4 h-4 text-[#23F7DD]" />
                            <span className="text-xs text-[#64748B]">Address <span className="text-red-400">*</span></span>
                          </div>
                          <p className={`font-medium ${info?.address ? 'text-white' : 'text-red-400 italic'}`}>
                            {info?.address || "Not provided"}
                          </p>
                        </div>
                        
                        {/* Phone */}
                        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                          <div className="flex items-center gap-2 mb-1">
                            <Phone className="w-4 h-4 text-[#23F7DD]" />
                            <span className="text-xs text-[#64748B]">Phone Number</span>
                          </div>
                          <p className="text-white font-medium">{info?.phone || "—"}</p>
                        </div>
                        
                        {/* Bank Account */}
                        <div className="p-3 bg-white/5 rounded-lg border border-white/10">
                          <div className="flex items-center gap-2 mb-1">
                            <CreditCard className="w-4 h-4 text-[#23F7DD]" />
                            <span className="text-xs text-[#64748B]">Bank Account</span>
                          </div>
                          <p className="text-white font-medium">{info?.bank_account || "—"}</p>
                        </div>
                        
                        {/* Nationality */}
                        <div className="p-3 bg-white/5 rounded-lg border border-white/10 md:col-span-2">
                          <div className="flex items-center gap-2 mb-1">
                            <Globe className="w-4 h-4 text-[#23F7DD]" />
                            <span className="text-xs text-[#64748B]">Nationality</span>
                          </div>
                          <p className="text-white font-medium">{info?.nationality || "—"}</p>
                        </div>
                      </div>
                      
                      {!isComplete && (
                        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-red-400 font-medium">Profile Incomplete</p>
                              <p className="text-sm text-[#94A3B8] mt-1">
                                Please complete your profile with the required information (Full Name, Personal Number, and Address) before signing the contract.
                              </p>
                              <Button
                                onClick={() => window.location.href = '/worker/profile'}
                                className="mt-3 bg-red-500 hover:bg-red-600 text-white"
                                size="sm"
                              >
                                Complete Profile Now
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {isComplete && (
                        <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <p className="text-sm text-green-400 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            Profile complete! You can sign the contract below.
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Show Personal Info Summary if already signed */}
          {contract.worker_signed && contract.personal_info && (
            <Card className="bg-[#0F0F0F] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <UserCircle className="w-5 h-5 text-[#23F7DD]" />
                  Personal Information (Recorded)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-xs text-[#64748B]">Full Name</p>
                    <p className="text-white">{contract.personal_info.full_name || "—"}</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-xs text-[#64748B]">Personal Number</p>
                    <p className="text-white">{contract.personal_info.personal_number || "—"}</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg md:col-span-2">
                    <p className="text-xs text-[#64748B]">Address</p>
                    <p className="text-white">{contract.personal_info.address || "—"}</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-xs text-[#64748B]">Phone</p>
                    <p className="text-white">{contract.personal_info.phone || "—"}</p>
                  </div>
                  <div className="p-3 bg-white/5 rounded-lg">
                    <p className="text-xs text-[#64748B]">Bank Account</p>
                    <p className="text-white">{contract.personal_info.bank_account || "—"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contract Preview */}
          <Card className="bg-[#0F0F0F] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#23F7DD]" />
                Contract Terms (v2.0)
              </CardTitle>
              <CardDescription>Permanent Employment Without Guaranteed Working Hours – On-Call</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="prose prose-invert max-w-none text-sm space-y-4">
                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">1. Position</h4>
                  <p className="text-[#94A3B8]">
                    You are employed as <strong className="text-white">{contract.position || "Scaffolder / Construction Worker"}</strong>.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">2. Type of Employment</h4>
                  <p className="text-[#94A3B8]">
                    You are <strong className="text-white">permanently employed without guaranteed working hours</strong> (on-call employment).
                  </p>
                  <ul className="list-disc list-inside text-[#94A3B8] mt-2 space-y-1">
                    <li>Work is offered on a project-by-project basis.</li>
                    <li>You are free to accept or decline assignments without any obligation, and no minimum work is guaranteed.</li>
                  </ul>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">3. Working Hours</h4>
                  <p className="text-[#94A3B8]">There are no guaranteed working hours.</p>
                  <ul className="list-disc list-inside text-[#94A3B8] mt-2 space-y-1">
                    <li>Working time applies only to accepted and scheduled assignments.</li>
                    <li>You determine availability and may refuse assignments without consequences.</li>
                  </ul>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">4. Place of Work</h4>
                  <p className="text-[#94A3B8]">
                    Work may take place at various construction sites in Norway, depending on project location.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">5. Compensation</h4>
                  <p className="text-[#94A3B8]">Compensation is agreed per project or per assignment.</p>
                  <ul className="list-disc list-inside text-[#94A3B8] mt-2 space-y-1">
                    <li>Any stated project duration is an <strong className="text-yellow-400">estimate only</strong> and does not constitute guaranteed working days.</li>
                    <li>Payment applies only to work actually performed and approved.</li>
                    <li>Payment is made within 14 days after approved completion.</li>
                    <li>Rates may vary depending on project scope, duration, and required competence.</li>
                  </ul>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">6. Tax and Employer Contributions</h4>
                  <p className="text-[#94A3B8]">AllStillas AS is responsible for:</p>
                  <ul className="list-disc list-inside text-[#94A3B8] mt-2 space-y-1">
                    <li>withholding tax</li>
                    <li>paying employer contributions</li>
                    <li>holiday pay (feriepenger)</li>
                    <li>mandatory pension contributions</li>
                  </ul>
                  <p className="text-[#94A3B8] mt-2">in accordance with Norwegian law.</p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">7. Holiday Pay</h4>
                  <p className="text-[#94A3B8]">
                    Holiday pay is earned and paid in accordance with the Norwegian Holiday Act.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">8. Sick Leave</h4>
                  <p className="text-[#94A3B8]">Sick pay applies only to accepted and scheduled assignments, in accordance with Norwegian law.</p>
                  <ul className="list-disc list-inside text-[#94A3B8] mt-2 space-y-1">
                    <li>No sick pay is due for estimated, unconfirmed, or non-scheduled working days.</li>
                    <li>Sick leave must be reported immediately and documented.</li>
                  </ul>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">9. Equipment and Tools</h4>
                  <ul className="list-disc list-inside text-[#94A3B8] space-y-1">
                    <li>You are responsible for providing personal protective equipment and basic tools.</li>
                    <li>Tool rental may be offered when needed.</li>
                    <li>AllStillas branded workwear may be provided but is not mandatory.</li>
                  </ul>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">10. Health, Safety and Environment (HSE / HMS)</h4>
                  <p className="text-[#94A3B8]">
                    You must comply with all applicable HSE (HMS) rules, instructions, and safety requirements at each worksite.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">11. Other Employment</h4>
                  <p className="text-[#94A3B8]">
                    You are free to work for other employers outside accepted assignments with AllStillas AS.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">12. Termination</h4>
                  <p className="text-[#94A3B8]">
                    Either party may terminate this employment with <strong className="text-white">14 days&apos; written notice</strong>.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">13. Confidentiality</h4>
                  <p className="text-[#94A3B8]">
                    You shall not disclose confidential information related to AllStillas AS, its clients, or projects during or after employment.
                  </p>
                </div>

                <div className="p-4 bg-white/5 rounded-lg">
                  <h4 className="text-[#23F7DD] font-semibold mb-2">14. Governing Law</h4>
                  <p className="text-[#94A3B8]">
                    This Agreement is governed by and interpreted in accordance with Norwegian law.
                  </p>
                </div>

                {/* Confirmation of Understanding */}
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <h4 className="text-blue-400 font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Confirmation of Understanding
                  </h4>
                  <p className="text-[#94A3B8] italic mb-4">
                    The employee confirms that this agreement has been provided in a language they understand and that they have had the opportunity to ask questions before signing.
                  </p>
                  
                  {/* Terms Acceptance Checkbox - Only show if not yet signed */}
                  {!contract?.worker_signed && (
                    <label className="flex items-start gap-3 cursor-pointer p-3 bg-[#23F7DD]/10 border border-[#23F7DD]/30 rounded-lg mt-4">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="mt-1 w-5 h-5 rounded border-white/30 bg-white/10 text-[#23F7DD] focus:ring-[#23F7DD] focus:ring-offset-0 cursor-pointer"
                        data-testid="terms-checkbox"
                      />
                      <span className="text-sm text-white">
                        <strong className="text-[#23F7DD]">I accept the terms and conditions</strong>
                        <br />
                        <span className="text-[#94A3B8]">
                          I confirm that I have read and understood all the terms of this employment contract and 
                          agree to abide by them. I understand that this is a permanent on-call employment without 
                          guaranteed working hours.
                        </span>
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Signature Pad Modal */}
          {showSignaturePad && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <Card className="bg-[#0F0F0F] border-white/10 w-full max-w-lg">
                <CardHeader>
                  <CardTitle className="text-white">Sign Your Contract</CardTitle>
                  <CardDescription>Draw your signature below using your mouse or finger</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Agreement notice */}
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-[#94A3B8]">
                        By signing this contract, you agree to the terms of employment as a permanent on-call worker 
                        with AllStillas AS. You confirm that you have read and understood all contract terms.
                      </p>
                    </div>
                  </div>

                  {/* Signature Canvas */}
                  <div className="border-2 border-dashed border-white/20 rounded-lg p-2 bg-white">
                    <canvas
                      ref={canvasRef}
                      width={400}
                      height={150}
                      className="w-full cursor-crosshair touch-none"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>

                  <div className="flex justify-between">
                    <Button
                      variant="ghost"
                      onClick={clearSignature}
                      className="text-[#94A3B8] hover:text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear
                    </Button>
                    <p className="text-sm text-[#94A3B8]">
                      {hasSignature ? "✓ Signature drawn" : "Draw your signature above"}
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowSignaturePad(false)}
                      className="flex-1 border-white/20 text-white hover:bg-white/10"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSign}
                      disabled={!hasSignature || signing}
                      className="flex-1 bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] disabled:opacity-50"
                      data-testid="confirm-sign-btn"
                    >
                      {signing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Signing...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Sign and Save Contract
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      ) : (
        <Card className="bg-[#0F0F0F] border-white/10">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-[#94A3B8]">No employment contract found. Please complete your profile first.</p>
          </CardContent>
        </Card>
      )}
      </div>
    </DashboardLayout>
  );
}
